/**
 * derivePhaseStatusesFromInstance — canonical phase-status derivation.
 *
 * Canonical source of truth for both ora and the Outlook add-in.
 * Ported from apps/outlook-addin/src/components/PhasePipeline.tsx
 * (lines 268-315) and extended to also use step_index when the server
 * reports no phase history.
 *
 * Import paths:
 *   ora    → ~/common/workflows/phaseStatus
 *   add-in → @product-app/common/workflows/phaseStatus
 */

import type { InstanceStatusSnapshot, PhaseDefinition, PhaseStatus } from "./types";

export type { PhaseStatus, PhaseDefinition };

/**
 * Derive per-phase status from a live instance snapshot.
 *
 * Priority order:
 * 1. `phases` history — phases with an `ended_at` are complete (both hosts)
 * 2. step_index numeric index (no history reported — INFERS completion)
 * 3. current_phase string id match (fallback for both)
 * 4. Terminal status collapses all phases (completed/cancelled)
 */
export function derivePhaseStatusesFromInstance(
  phases: PhaseDefinition[],
  instance: InstanceStatusSnapshot | null,
): PhaseStatus[] {
  if (!instance || phases.length === 0) {
    return phases.map((p) => ({ phase_id: p.id, state: "pending" }));
  }

  // Substrate migration 019 — `instance.phases` carries per-phase Start /
  // End timestamps. Merge them into whichever derivation path runs below
  // so the per-step tooltip can show duration. Old instances pre-date the
  // migration and have no phases array → ts stay undefined → tooltip
  // gracefully omits the timing rows.
  const tsByPhase = new Map<string, { started_at: string | null; ended_at: string | null }>();
  if (instance.phases) {
    for (const row of instance.phases) {
      tsByPhase.set(row.phase_id, {
        started_at: row.started_at,
        ended_at: row.ended_at,
      });
    }
  }
  const withTs = (s: PhaseStatus): PhaseStatus => {
    const ts = tsByPhase.get(s.phase_id);
    if (!ts) return s;
    return { ...s, started_at: ts.started_at, ended_at: ts.ended_at };
  };

  const statusToken = instance.status?.toLowerCase() ?? "";
  const isCompleted = statusToken === "completed";
  const isCancelled = statusToken === "cancelled";
  const isTerminalFail =
    statusToken === "failed" || statusToken === "timeout_cancelled";
  // Keep in sync with statusMerge.isHitlStatus + pickDrawerStatusFamily.
  // `rpa_review` is a HITL state (review-checkpoint waiting on operator)
  // even though it lives outside the paused_hitl/warm_hitl pair — without
  // it here, the current phase renders as "pending" instead of
  // "hitl_waiting" and the pipeline doesn't visually mark the HITL gate.
  // `pending_edocs` is the carrier eDoc cold wait — a parked, non-terminal
  // gate. Marked "hitl_waiting" so the pipeline shows the eDoc step as the
  // active waiting node (the calm drawer body, not the Input-Needed form,
  // communicates that no AM action is required).
  const isPaused =
    statusToken === "paused_hitl"
    || statusToken === "warm_hitl"
    || statusToken === "rpa_review"
    || statusToken === "pending_edocs";
  // pending_email_send is non-terminal (AM needs to send email) — treat like
  // running so phases are not collapsed to complete.
  const isRunning = statusToken === "running" || statusToken === "pending_email_send";

  const errorMsg = instance.error_message ?? instance.error ?? null;

  // --- Path A: the server's own phase history (migration 019) ---
  //
  // A phase is COMPLETE when its history row has an `ended_at`. This used to
  // read `instance.phase_outputs` — a field no code path has ever emitted (the
  // substrate has no per-phase output store; `workflow_instance_phase_history`
  // holds only instance_id / phase_id / started_at / ended_at, and neither the
  // product API nor the SignalR push carries such a key). So this branch never
  // ran in EITHER host and both silently fell through to Path B, which INFERS
  // completion from a step counter. On a run that branches or skips — which
  // EMUW does constantly — that inference marks rows complete that never ran.
  //
  // `instance.phases` is the real thing, is already published, and is already
  // parsed into `tsByPhase` above, so the completed set costs no extra pass.
  // Gated on a non-empty history rather than on "something has ended": a run
  // whose first phase is still going has rows but no `ended_at`, and the
  // per-index arms below already handle that correctly.
  // Excludes terminal FAILURES on purpose: Path E below already reads this
  // same history for them and encodes a judgement this path does not have —
  // on a failed run the last phase present in history is the one that failed,
  // even though its row may carry an `ended_at`. Running / paused / completed
  // / cancelled runs come here.
  if (!isTerminalFail && instance.phases && instance.phases.length > 0) {
    // Two distinct facts, and the difference is the whole point: a phase with
    // an `ended_at` finished; a phase with a history row but no `ended_at`
    // started and did not; a phase with NO row never ran at all.
    const executedSet = new Set(instance.phases.map((row) => row.phase_id));
    const completedSet = new Set(
      [...tsByPhase.entries()]
        .filter(([, ts]) => ts.ended_at !== null)
        .map(([phaseId]) => phaseId),
    );
    const currentIdx = instance.current_phase
      ? phases.findIndex((p) => p.id === instance.current_phase)
      : -1;

    return phases.map((p, i) => {
      if (completedSet.has(p.id)) {
        return withTs({ phase_id: p.id, state: "completed" });
      }
      if (i === currentIdx) {
        if (isPaused) return withTs({ phase_id: p.id, state: "hitl_waiting" });
        if (isCancelled) return withTs({ phase_id: p.id, state: "pending" });
        if (isCompleted) return withTs({ phase_id: p.id, state: "completed" });
        return withTs({ phase_id: p.id, state: "running" });
      }
      // Started, never ended, and not where the run says it is — a nested or
      // parallel phase. Still going on a live run; on a terminal one it simply
      // never finished, which is not the same as having completed.
      //
      // On a terminal run this is reported as "skipped", NOT "pending".
      // "pending" is the vocabulary for an UPCOMING phase — a plain dimmed dot
      // the reader scans past — and a run that is over has nothing upcoming.
      // Saying "pending" here claims the phase never started, when its history
      // row proves it did and its missing `ended_at` proves it never finished.
      // `skipped` is the only member of the PhaseStatus union (types.ts) that
      // reads as "this did not happen / did not complete": it renders its own
      // grey "Skipped" pill rather than an upcoming dot, so an abandoned phase
      // is visible as something that went unfinished. It is a slightly wider
      // reading of the word than Path E's unreached phases — the two are both
      // "did not complete", which is the fact the pipeline is reporting — and
      // a NEW union member is not available here: `STATE_TONE` in
      // WorkflowPhasePipeline.tsx is keyed exhaustively on this union, so a
      // seventh state is a cross-file change beyond this fix.
      if (executedSet.has(p.id)) {
        return withTs({
          phase_id: p.id,
          state: isCompleted || isCancelled ? "skipped" : "running",
        });
      }
      // NO history row: this phase never ran. The previous version answered
      // "completed" here whenever the phase sat before `current_phase`, or
      // whenever the run was completed — which is exactly how a branching run
      // (EMUW skips phases constantly) ended up showing green checkmarks for
      // work that never happened. A finished run that never entered a phase
      // SKIPPED it; a live run may still get there, so that stays pending.
      // Same vocabulary as Path E, which already reports unreached phases
      // this way.
      return withTs({ phase_id: p.id, state: isCompleted ? "skipped" : "pending" });
    });
  }

  // --- Path B: no phase history — INFER from step_index ---
  // Reached only by instances that pre-date migration 019 or have not entered
  // an instrumented phase yet. Everything else takes Path A above.
  const stepIndex = instance.step_index ?? null;
  const isTerminal = isCompleted || isCancelled || isTerminalFail;
  // A TERMINAL status can arrive with a STALE, out-of-range step_index: the
  // substrate sends step_index=null on cancel, but the client merge preserves the
  // previously-advanced max (statusMerge maxNum / useInstancePush refetch fallback).
  // With stepIndex >= phases.length the `i === stepIndex` boundary below is never
  // reached, so every phase falls into the `i < stepIndex` → "completed" bucket —
  // which is why a CANCELLED run rendered ALL steps green. For a terminal status
  // with an out-of-range index, skip Path B and let Path D render the terminal
  // state correctly (cancelled → all pending, completed → all completed, failed →
  // last failed). Running/paused keep Path B regardless of the index.
  if (stepIndex !== null && (!isTerminal || (stepIndex >= 0 && stepIndex < phases.length))) {
    return phases.map((_, i) => {
      if (i < stepIndex) return withTs({ phase_id: phases[i].id, state: "completed" });
      if (i === stepIndex) {
        if (isTerminalFail)
          return withTs({ phase_id: phases[i].id, state: "failed", error_message: errorMsg });
        if (isPaused) return withTs({ phase_id: phases[i].id, state: "hitl_waiting" });
        if (isCompleted) return withTs({ phase_id: phases[i].id, state: "completed" });
        if (isRunning) return withTs({ phase_id: phases[i].id, state: "running" });
        return withTs({ phase_id: phases[i].id, state: "pending" });
      }
      if (isCompleted) return withTs({ phase_id: phases[i].id, state: "completed" });
      return withTs({ phase_id: phases[i].id, state: "pending" });
    });
  }

  // --- Path C: current_phase string id only ---
  const currentPhase = instance.current_phase ?? null;
  if (currentPhase) {
    let foundIdx = -1;
    const result: PhaseStatus[] = phases.map((p, i) => {
      if (p.id === currentPhase || p.id.includes(currentPhase)) {
        foundIdx = i;
        if (isTerminalFail) return withTs({ phase_id: p.id, state: "failed" as const, error_message: errorMsg });
        if (isPaused) return withTs({ phase_id: p.id, state: "hitl_waiting" as const });
        if (isCancelled) return withTs({ phase_id: p.id, state: "pending" as const });
        return withTs({ phase_id: p.id, state: "running" as const });
      }
      return withTs({ phase_id: p.id, state: "pending" as const });
    });
    // Mark phases before current as completed
    if (foundIdx > 0) {
      for (let i = 0; i < foundIdx; i++) {
        result[i] = withTs({ phase_id: phases[i].id, state: "completed" });
      }
    }
    if (isCompleted) {
      return phases.map((p) => withTs({ phase_id: p.id, state: "completed" as const }));
    }
    return result;
  }

  // --- Path E: terminal fail + phase history (migration 019) ---
  // When both step_index and current_phase are absent (e.g. run_graph_phase
  // returned via the exception path before _try_persist_current_phase ran),
  // use workflow_instance_phase_history to find which phases actually executed.
  // Phases not in history were never reached → "skipped".
  // The last executed phase in manifest order is the one that failed.
  if (isTerminalFail && instance.phases && instance.phases.length > 0) {
    const executedSet = new Set(instance.phases.map((p) => p.phase_id));
    let lastExecutedIdx = -1;
    for (let i = phases.length - 1; i >= 0; i--) {
      if (executedSet.has(phases[i].id)) {
        lastExecutedIdx = i;
        break;
      }
    }
    if (lastExecutedIdx >= 0) {
      return phases.map((p, i) => {
        if (!executedSet.has(p.id)) {
          return withTs({ phase_id: p.id, state: "skipped" });
        }
        if (i === lastExecutedIdx) {
          return withTs({ phase_id: p.id, state: "failed", error_message: errorMsg });
        }
        return withTs({ phase_id: p.id, state: "completed" });
      });
    }
  }

  // --- Path D: terminal with no progress info ---
  if (isCompleted) {
    return phases.map((p) => withTs({ phase_id: p.id, state: "completed" }));
  }
  if (isTerminalFail) {
    return phases.map((p, i) =>
      i === phases.length - 1
        ? withTs({ phase_id: p.id, state: "failed", error_message: errorMsg })
        : withTs({ phase_id: p.id, state: "completed" }),
    );
  }

  return phases.map((p) => withTs({ phase_id: p.id, state: "pending" }));
}
